import { Table, TableBody, TableHeader, TableRow, TableHead, TableCell } from "../../../components/ui/table";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useInventoryContext } from "../../../hooks/use-inventory";
import { useIsMobile } from "../../../hooks/use-mobile";
import { InventoryItem } from "../../../lib/types";
import {
  ArrowUpAz,
  ArrowDownAz,
  PlusIcon,
  TrashIcon,
  MoveIcon,
  SquareStack,
  Barcode,
  Box,
  Filter
} from "lucide-react";
import { InventoryDialog } from "./intentory-dialog";
import { SearchInput } from "../../../components/molecules";
import {
  Button,
  Checkbox,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectValue,
  DeleteDialog
} from "../../../components/ui/";
import { SelectTrigger } from "@radix-ui/react-select";
import { CollectionsEnum, SubscriptionTypeEnum } from "../../../lib/enums";
import { MoveInventoryDialog } from "./inventory-move-dialog";
import { Tooltip } from "../../../components/molecules";
import { useSubscriptionTooling } from "../../../hooks/use-subscription";
import { BarcodePrintDialog } from "./barcode-print-dialog";
import { toast } from "sonner";
import { Units } from "../units/units";
import { useUserContext } from "../../../hooks/use-user";
import { canAddInventoryItem, getRemainingInventorySlots, getFeatureLimits } from "../../../lib/subscription-limits";
import CollapsibleSection from "../../../components/ui/collapsible-section";

export const InventoryTable: FC = ({ }) => {

  const { deleteInventoryElem, unitNameMap, unitAbbrMap, categoryMap, binLocationMap, inventory } = useInventoryContext()
  const { hasAccess } = useSubscriptionTooling()
  const { subscription } = useUserContext()
  const isMobile = useIsMobile()

  // Subscription limits
  const canAdd = canAddInventoryItem(inventory.length, subscription)
  const remainingSlots = getRemainingInventorySlots(inventory.length, subscription)
  const limits = getFeatureLimits(subscription)

  const [tableData, setTableData] = useState<InventoryItem[]>([])
  const [isAsc, setIsAsc] = useState(true)
  const [sortCol, setSortCol] = useState("name")
  const [showInvDialog, setShowInvDialog] = useState(false)
  const [editItemId, setEditItemId] = useState("")
  const [catFilter, setCatFilter] = useState("All")
  const [unitFilter, setUnitFilter] = useState("All")
  const [selectedTableRows, setSelectedTableRows] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState("")

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showMoveDialog, setShowMoveDialog] = useState(false)
  const [showPrintBarcodesDialog, setShowPrintBarcodesDialog] = useState(false)


  const columnHeaders = useMemo(() => {
    const headers = [
      {
        name: "SKU",
        acc: "sku",
        isVisible: true,
        renderCell: (item: InventoryItem) => {
          return <TableCell>
            <div
              className="hover:underline"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(item.sku);
                  toast.info("copied to clipboard")
                } catch (err) {
                  console.error('Failed to copy: ', err);
                }
              }}>
              {item.sku}
            </div>
          </TableCell>
        },

      },
      {
        name: "Name",
        acc: "name",
        isVisible: true,
        renderCell: (item: InventoryItem) => {
          return <TableCell
            className="hover:underline cursor-pointer"
            onClick={() => {
              setEditItemId(item.id)
              setShowInvDialog(true)
            }}>
            {item.name}
          </TableCell>
        },
      },
      {
        name: "Value",
        acc: "value",
        isVisible: !isMobile,
        renderCell: (item: InventoryItem) => {
          return <TableCell className="w-fit text-left">${" "}{Number(item.value).toFixed(2)}</TableCell>
        },
      },
      {
        name: "Qty",
        acc: "quantity",
        isVisible: !isMobile,
        renderCell: (item: InventoryItem) => {
          return <TableCell>{item.quantity}</TableCell>
        }
      },
      {
        name: "Category",
        acc: "category",
        isVisible: true,
        renderCell: (item: InventoryItem) => {
          return <TableCell>{categoryMap[item.category]}</TableCell>
        }
      },
      {
        name: "Unit",
        acc: "unit",
        isVisible: !isMobile,
        renderCell: (item: InventoryItem) => {
          return <TableCell>{unitNameMap[item.unit]}</TableCell>
        }
      },

      {
        name: "Location",
        acc: "location",
        isVisible: true,
        renderCell: (item: InventoryItem) => {
          return <TableCell>{Object.keys(unitAbbrMap).includes(item.unit) && item?.location ? `${unitAbbrMap[item.unit]}-${binLocationMap[item.location]}` : ""}</TableCell>
        }
      },
    ]
    return headers
  }, [isMobile, categoryMap])

  useEffect(() => {
    sortBy()
  }, [inventory])

  useEffect(() => {
    if (catFilter === "All" && unitFilter === "All") {
      sortBy()
    } else if (catFilter === "All" && unitFilter !== "All") {
      sortBy(inventory.filter((i) => i.unit === unitFilter))
    } else if (catFilter !== "All" && unitFilter === "All") {
      sortBy(inventory.filter((i) => i.category === catFilter))
    } else {
      sortBy(inventory.filter((i) => i.unit === unitFilter && i.category === catFilter))
    }
  }, [catFilter, unitFilter])

  // Handle search query changes
  useEffect(() => {
    if (searchQuery) {
      setTableData(search(searchQuery))
    } else {
      sortBy()
    }
  }, [searchQuery])

  const sortBy = useCallback((sortArr: InventoryItem[] = inventory) => {
    const sorted = sortArr.sort((a: any, b: any) => {
      const aComp: string = String(a[sortCol]).toLowerCase()
      const bComp: string = String(b[sortCol]).toLowerCase()

      if (aComp > bComp) {
        return isAsc ? 0 : -1
      } else {
        return isAsc ? -1 : 0
      }
    })
    setTableData(sorted)

  }, [inventory, isAsc])

  const search = useCallback((
    query: string,
  ): InventoryItem[] => {

    // Normalize query and target strings based on case sensitivity
    const normalizedQuery = query.toLowerCase();
    const terms = normalizedQuery.split(/\s+/); // Split query into words

    return inventory.filter((target) => {
      const normalizedTargetOne = (target.name.toLowerCase()).trim();
      const normatizedTargetTwo = target.sku?.toLowerCase()?.trim() || "";


      // Check if all terms or any term matches based on the option
      const matches = terms.map((term) => normalizedTargetOne.includes(term) || normatizedTargetTwo?.includes(term));
      return matches.every(Boolean)
    });
  }, [inventory])


  return (
    <div className="relative">
      {/* Toolbar */}
      <div className="sticky top-0 z-50 -mx-6 backdrop-blur-lg bg-white/80 dark:bg-neutral900/80 border-b border-neutral200/50 dark:border-neutral700/50">
        {!isMobile && (
          <div className="px-6 pt-6 pb-6">
            <div className="max-w-7xl mx-auto space-y-4">
              {/* Desktop: Always visible toolbar */}
              <div className="flex gap-3 items-center">
                {!canAdd ? (
                  <Tooltip message={`You've reached your plan limit of ${limits.maxInventoryItems} items. Upgrade to add more.`}>
                    <Button
                      variant="primaryGlow"
                      size="lg"
                      onClick={() => {
                        toast.error(`You've reached your plan limit of ${limits.maxInventoryItems} items. Upgrade to add more!`)
                      }}
                      disabled={true}
                    >
                      <PlusIcon stroke="currentColor" width={20} height={20} />
                      <span className="font-semibold">Add Item</span>
                      {remainingSlots !== null && (
                        <span className="ml-2 text-xs opacity-75">
                          ({remainingSlots} left)
                        </span>
                      )}
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    variant="primaryGlow"
                    size="lg"
                    onClick={() => setShowInvDialog(true)}
                  >
                    <PlusIcon stroke="currentColor" width={20} height={20} />
                    <span className="font-semibold">Add Item</span>
                    {remainingSlots !== null && (
                      <span className="ml-2 text-xs opacity-75">
                        ({remainingSlots} left)
                      </span>
                    )}
                  </Button>
                )}

                <SearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search by name or SKU..."
                  className="flex-1"
                />
              </div>

              <div className="flex gap-3 flex-wrap items-center">
                <div className="flex items-center gap-2 text-sm font-semibold text-neutral600 dark:text-neutral400">
                  <Filter width={16} height={16} />
                  <span>Filters:</span>
                </div>

                <Select
                  defaultValue="All"
                  onValueChange={(value: string) => {
                    setUnitFilter(value)
                  }}
                >
                  <SelectTrigger className="backdrop-blur-md bg-white/60 dark:bg-neutral800/60 border-2 border-neutral200/50 dark:border-neutral700/50 hover:border-sumoPrimary/50 dark:hover:border-sumoPrimary/50 hover:bg-white/80 dark:hover:bg-neutral800/80 rounded-full px-4 py-2.5 shadow-soft transition-all duration-300 dark:text-neutral100 font-medium text-sm min-w-[140px]">
                    <SelectValue placeholder="Unit Filter" />
                  </SelectTrigger>
                  <SelectContent className="border-2 border-neutral200 dark:border-neutral700 rounded-lg shadow-xl bg-white dark:bg-neutral800">
                    <SelectGroup>
                      <SelectItem value="All">All Units</SelectItem>
                      {Object.keys(unitNameMap).map((u) => {
                        return <SelectItem value={u} key={u}>{unitNameMap[u]}</SelectItem>
                      })}
                    </SelectGroup>
                  </SelectContent>
                </Select>

                <Select
                  defaultValue="All"
                  onValueChange={(value: string) => {
                    setCatFilter(value)
                  }}
                >
                  <SelectTrigger className="backdrop-blur-md bg-white/60 dark:bg-neutral800/60 border-2 border-neutral200/50 dark:border-neutral700/50 hover:border-sumoPrimary/50 dark:hover:border-sumoPrimary/50 hover:bg-white/80 dark:hover:bg-neutral800/80 rounded-full px-4 py-2.5 shadow-soft transition-all duration-300 dark:text-neutral100 font-medium text-sm min-w-[160px]">
                    <SelectValue placeholder="Category Filter" />
                  </SelectTrigger>
                  <SelectContent className="border-2 border-neutral200 dark:border-neutral700 rounded-lg shadow-xl bg-white dark:bg-neutral800">
                    <SelectGroup>
                      <SelectItem value="All">All Categories</SelectItem>
                      {Object.keys(categoryMap).map((cat) => {
                        return <SelectItem value={cat} key={cat}>{categoryMap[cat]}</SelectItem>
                      })}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {isMobile && (
          <div>
            {/* Mobile: Add button always visible */}
            <div className="px-6 pt-4 pb-3">
              {!canAdd ? (
                <Tooltip message={`You've reached your plan limit of ${limits.maxInventoryItems} items. Upgrade to add more.`}>
                  <Button
                    variant="primaryGlow"
                    size="lg"
                    className="w-full"
                    onClick={() => {
                      toast.error(`You've reached your plan limit of ${limits.maxInventoryItems} items. Upgrade to add more!`)
                    }}
                    disabled={true}
                  >
                    <PlusIcon stroke="currentColor" width={20} height={20} />
                    <span className="font-semibold">Add Item</span>
                    {remainingSlots !== null && (
                      <span className="ml-2 text-xs opacity-75">
                        ({remainingSlots} left)
                      </span>
                    )}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  variant="primaryGlow"
                  size="lg"
                  className="w-full"
                  onClick={() => setShowInvDialog(true)}
                >
                  <PlusIcon stroke="currentColor" width={20} height={20} />
                  <span className="font-semibold">Add Item</span>
                  {remainingSlots !== null && (
                    <span className="ml-2 text-xs opacity-75">
                      ({remainingSlots} left)
                    </span>
                  )}
                </Button>
              )}
            </div>

            {/* Mobile: Collapsible filters and search */}
            <CollapsibleSection title="Filters & Search" defaultOpen={false} className="border-t">
              <div className="space-y-4">
                <SearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search by name or SKU..."
                  className="w-full"
                />

                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-neutral600 dark:text-neutral400">
                    <Filter width={16} height={16} />
                    <span>Filters:</span>
                  </div>

                  <Select
                    defaultValue="All"
                    onValueChange={(value: string) => {
                      setUnitFilter(value)
                    }}
                  >
                    <SelectTrigger className="backdrop-blur-md bg-white/60 dark:bg-neutral800/60 border-2 border-neutral200/50 dark:border-neutral700/50 hover:border-sumoPrimary/50 dark:hover:border-sumoPrimary/50 hover:bg-white/80 dark:hover:bg-neutral800/80 rounded-full px-4 py-2.5 shadow-soft transition-all duration-300 dark:text-neutral100 font-medium text-sm w-full">
                      <SelectValue placeholder="Unit Filter" />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-neutral200 dark:border-neutral700 rounded-lg shadow-xl bg-white dark:bg-neutral800">
                      <SelectGroup>
                        <SelectItem value="All">All Units</SelectItem>
                        {Object.keys(unitNameMap).map((u) => {
                          return <SelectItem value={u} key={u}>{unitNameMap[u]}</SelectItem>
                        })}
                      </SelectGroup>
                    </SelectContent>
                  </Select>

                  <Select
                    defaultValue="All"
                    onValueChange={(value: string) => {
                      setCatFilter(value)
                    }}
                  >
                    <SelectTrigger className="backdrop-blur-md bg-white/60 dark:bg-neutral800/60 border-2 border-neutral200/50 dark:border-neutral700/50 hover:border-sumoPrimary/50 dark:hover:border-sumoPrimary/50 hover:bg-white/80 dark:hover:bg-neutral800/80 rounded-full px-4 py-2.5 shadow-soft transition-all duration-300 dark:text-neutral100 font-medium text-sm w-full">
                      <SelectValue placeholder="Category Filter" />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-neutral200 dark:border-neutral700 rounded-lg shadow-xl bg-white dark:bg-neutral800">
                      <SelectGroup>
                        <SelectItem value="All">All Categories</SelectItem>
                        {Object.keys(categoryMap).map((cat) => {
                          return <SelectItem value={cat} key={cat}>{categoryMap[cat]}</SelectItem>
                        })}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CollapsibleSection>
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="py-6">
        {
          selectedTableRows.length > 0 &&
          <div className="w-full mb-4 p-4 bg-sumoPrimary/10 dark:bg-sumoPrimary/20 border-2 border-sumoPrimary rounded-lg flex items-center gap-3 shadow-soft animate-fade-in">
            <span className="font-semibold text-neutral900 dark:text-neutral100">
              {selectedTableRows.length} item{selectedTableRows.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              <Tooltip message="delete selected">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => { setShowDeleteDialog(true) }}
                >
                  <TrashIcon width={16} height={16} />
                  {!isMobile && <span>Delete</span>}
                </Button>
              </Tooltip>

              <Tooltip message="move selected">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => { setShowMoveDialog(true) }}
                >
                  <SquareStack width={16} height={16} />
                  {!isMobile && <span>Move</span>}
                </Button>
              </Tooltip>

              <Tooltip message={!limits.canPrintBarcodes ? 'Upgrade to Premium to print barcodes' : 'Print barcodes for selected'}>
                <Button
                  variant="teal"
                  size="sm"
                  onClick={() => {
                    if (!limits.canPrintBarcodes) {
                      toast.error('Barcode printing is available in Premium and Enterprise tiers. Upgrade to unlock this feature.')
                      return
                    }
                    setShowPrintBarcodesDialog(true)
                  }}
                  disabled={!limits.canPrintBarcodes}
                >
                  <Barcode width={16} height={16} />
                  {!isMobile && <span>Barcodes</span>}
                </Button>
              </Tooltip>
            </div>
          </div>
        }
        <div className={`${isMobile ? "h-full" : "h-8/9"} `}>
          {
            tableData?.length > 0 && <Table >
              <TableHeader>
                <TableRow className="sticky top-0">
                  <TableHead>
                    <div className="flex h-full justify-content items-center">
                      <Checkbox checked={selectedTableRows.length === tableData.length} onClick={() => {
                        if (selectedTableRows.length === tableData.length) {
                          setSelectedTableRows([])
                        } else {
                          setSelectedTableRows(inventory.map((item) => item.id))
                        }
                      }} />
                    </div>
                  </TableHead>
                  {
                    columnHeaders.filter((h) => h.isVisible).map((header) => {
                      return <TableHead
                        className={`${header.acc === sortCol ? "font-bold text-neutral700 dark:text-neutral300" : "dark:text-neutral400"} `}
                        onClick={() => {
                          if (header.acc !== "location" && header.acc !== "quantity") {
                            setSortCol(header.acc)
                            sortBy()
                            setIsAsc(!isAsc)
                          }
                        }}
                        key={header.acc}
                      >
                        <div className="flex items-center gap-1">
                          {header.name}

                          {header.acc === sortCol ? (
                            <>
                              {isAsc ? <ArrowUpAz width={12} height={12} /> : <ArrowDownAz width={12} height={12} />}
                            </>
                          ) : (<div className="w-3"></div>)}
                        </div>
                      </TableHead>

                    })
                  }
                </TableRow>
              </TableHeader>

              <TableBody className="h-full overflow-hidden overflow-y-scroll">
                {tableData?.map((item) => {
                  return <TableRow key={item.id}>
                    <TableCell>
                      <div className="flex h-full justify-content items-center">
                        <Checkbox
                          checked={selectedTableRows.some((i) => { return i === item.id })}
                          onCheckedChange={() => {
                            if (!selectedTableRows.some((i) => i === item.id)) {
                              setSelectedTableRows((prev) => { return [...prev, item.id] })
                            } else {
                              setSelectedTableRows((prev) => { return prev.filter((i) => { return i !== item.id }) })
                            }
                          }}
                        />
                      </div>
                    </TableCell>

                    {columnHeaders.filter((h) => h.isVisible).map((h) => { return h.renderCell(item) })}
                  </TableRow>
                })}

              </TableBody>
            </Table>
          }
        </div>

        {
          tableData?.length === 0 && (
            <div className="w-full py-12 text-center bg-white dark:bg-neutral800 rounded-lg shadow-soft border border-neutral200 dark:border-neutral700">
              <div className="flex flex-col items-center gap-3">
                <Box className="text-neutral300 dark:text-neutral600" width={48} height={48} />
                <p className="text-lg font-semibold text-neutral500 dark:text-neutral400">No items found</p>
                <p className="text-sm text-neutral400 dark:text-neutral500">
                  {searchQuery ? `No items match "${searchQuery}"` : "Try adjusting your filters or add a new item"}
                </p>
              </div>
            </div>
          )
        }

      </div>

      <InventoryDialog
        isShowing={showInvDialog}
        editItemId={editItemId}
        close={() => {
          setShowInvDialog(false)
          setEditItemId("")
        }}
      />

      <DeleteDialog
        itemName="Inventory Item(s)"
        isShowing={showDeleteDialog}
        close={() => setShowDeleteDialog(false)}
        handleDelete={() => {
          if (selectedTableRows.length > 0) {
            selectedTableRows.forEach((id) => {
              deleteInventoryElem(id, CollectionsEnum.INVENTORY_ITEM)
            })
            setSelectedTableRows([])
          }
        }}
      />

      <MoveInventoryDialog
        isShowing={showMoveDialog}
        close={() => setShowMoveDialog(false)}
        selectedItems={inventory.filter((i) => selectedTableRows.includes(i.id))}
      />

      <BarcodePrintDialog
        selectedItems={inventory.filter((i) => selectedTableRows.includes(i.id))}
        isShowing={showPrintBarcodesDialog}
        close={() => {
          setShowPrintBarcodesDialog(false)
          setSelectedTableRows([])
        }}
      />
    </div>
  )
}
